import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectLabel,
} from "../../../components/ui/select";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../App";
import {
  Site,
  SiteFormData,
  STATE_OPTIONS,
  REGION_OPTIONS,
  LOCATION_TYPE_OPTIONS,
  LOCATION_STATUS_OPTIONS,
} from "../types/site";
import { stateMatches } from "../../../utils/state";
import {
  Plus,
  Search,
  Trash2,
  Edit2,
  Building2,
  MapPin,
  AlertCircle,
  X,
  Loader2,
} from "lucide-react";

export const SitesPage = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [totalSites, setTotalSites] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStateFilter, setSelectedStateFilter] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    siteId: string;
    locationCode: string;
  } | null>(null);
  const [editingSite, setEditingSite] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");

  const [formData, setFormData] = useState<SiteFormData>({
    siteId: "",
    locationCode: "",
    oldLocationCode: "",
    state: "",
    locationDescription: "",
    locationAddress: "",
    region: "",
    locationType: "",
    locationStatus: "",
  });

  const { authToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const maxResults = 10;

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.locationCode.trim()) newErrors.locationCode = "Location code is required";
    if (!formData.oldLocationCode.trim()) newErrors.oldLocationCode = "Old location code is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.locationDescription.trim()) newErrors.locationDescription = "Location description is required";
    if (!formData.locationAddress.trim()) newErrors.locationAddress = "Location address is required";
    if (!formData.region) newErrors.region = "Region is required";
    if (!formData.locationType) newErrors.locationType = "Location type is required";
    if (!formData.locationStatus) newErrors.locationStatus = "Location status is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fetch sites
  const fetchSites = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}account/${id}/sites?page=${page}&limit=${maxResults}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setSites(response.data.sites || []);
      setTotalSites(response.data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching sites:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken && id) {
      fetchSites();
    }
  }, [page, authToken, id]);

  // Create site
  const handleCreateSite = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        locationCode: formData.locationCode,
        oldLocationCode: formData.oldLocationCode,
        state: formData.state,
        locationDescription: formData.locationDescription,
        locationAddress: formData.locationAddress,
        region: formData.region,
        locationType: formData.locationType,
        locationStatus: formData.locationStatus,
      };

      if (editingSite) {
        // Update
        await axios.put(
          `${API_BASE_URL}account/${id}/sites/${editingSite}/update`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setSuccessMessage("Site updated successfully");
      } else {
        // Create
        await axios.post(
          `${API_BASE_URL}account/${id}/sites/create`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setSuccessMessage("Site created successfully");
      }

      // Reset form
      setFormData({
        siteId: "",
        locationCode: "",
        oldLocationCode: "",
        state: "",
        locationDescription: "",
        locationAddress: "",
        region: "",
        locationType: "",
        locationStatus: "",
      });
      setErrors({});
      setIsDrawerOpen(false);
      setEditingSite(null);
      setIsCreating(false);

      // Refresh list
      await fetchSites();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error: any) {
      console.error("Error saving site:", error);
      setErrors({
        submit: error.response?.data?.message || "Error saving site",
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete site
  const handleDeleteSite = async () => {
    if (!deleteConfirm) return;

    setLoading(true);
    try {
      await axios.delete(
        `${API_BASE_URL}account/${id}/sites/${deleteConfirm.siteId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setSuccessMessage("Site deleted successfully");
      setDeleteConfirm(null);
      await fetchSites();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error: any) {
      console.error("Error deleting site:", error);
      setErrors({
        submit: error.response?.data?.message || "Error deleting site",
      });
    } finally {
      setLoading(false);
    }
  };

  // Edit site
  const handleEditSite = (site: Site) => {
    setEditingSite(site.siteId);
    setFormData({
      siteId: site.siteId,
      locationCode: site.locationCode,
      oldLocationCode: site.oldLocationCode,
      state: site.state,
      locationDescription: site.locationDescription,
      locationAddress: site.locationAddress,
      region: site.region,
      locationType: site.locationType,
      locationStatus: site.locationStatus,
    });
    setIsDrawerOpen(true);
    setIsCreating(true);
  };

  // Close drawer
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setIsCreating(false);
    setEditingSite(null);
    setFormData({
      siteId: "",
      locationCode: "",
      oldLocationCode: "",
      state: "",
      locationDescription: "",
      locationAddress: "",
      region: "",
      locationType: "",
      locationStatus: "",
    });
    setErrors({});
  };

  // Fetch all sites for search
  const [allSites, setAllSites] = useState<Site[]>([]);
  
  useEffect(() => {
    const fetchAllSites = async () => {
      try {
        const pageSize = 500;
        const headers = { Authorization: `Bearer ${authToken}` };
        const firstResponse = await axios.get(
          `${API_BASE_URL}account/${id}/sites?page=1&limit=${pageSize}`,
          { headers }
        );
        const totalCount = firstResponse.data.totalCount || 0;
        let allData: Site[] = [...(firstResponse.data.sites || [])];
        let pageNum = 2;

        while (allData.length < totalCount) {
          const response = await axios.get(
            `${API_BASE_URL}account/${id}/sites?page=${pageNum}&limit=${pageSize}`,
            { headers }
          );
          allData = [...allData, ...(response.data.sites || [])];
          pageNum++;
        }

        setAllSites(allData);
      } catch (error) {
        console.error("Error fetching all sites:", error);
      }
    };

    if (authToken && id) {
      fetchAllSites();
    }
  }, [authToken, id]);

  const filteredSites = allSites.filter((site) => {
    const search = searchTerm.trim().toLowerCase();
    const stateLabel =
      STATE_OPTIONS.find((option) => stateMatches(site.state, option.value))
        ?.label.toLowerCase() || "";

    const matchesSearch =
      !search ||
      site.locationCode?.toLowerCase().includes(search) ||
      site.locationAddress?.toLowerCase().includes(search) ||
      site.locationDescription?.toLowerCase().includes(search) ||
      site.state?.toLowerCase().includes(search) ||
      stateLabel.includes(search);

    const matchesState =
      !selectedStateFilter || stateMatches(site.state, selectedStateFilter);

    return matchesSearch && matchesState;
  });

  // Get paginated records
  const getPaginatedRecords = (recordsToDisplay: Site[]) => {
    const startIndex = (page - 1) * maxResults;
    const endIndex = startIndex + maxResults;
    return recordsToDisplay.slice(startIndex, endIndex);
  };

  const displayedSites = getPaginatedRecords(filteredSites);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-lg shadow-lg">
              <Building2 className="text-white" size={28} />
            </div>
            <span>Sites Management</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your location sites and properties
          </p>
        </div>
        <Button
          onClick={() => {
            setIsDrawerOpen(true);
            setIsCreating(true);
            setEditingSite(null);
            setFormData({
              siteId: "",
              locationCode: "",
              oldLocationCode: "",
              state: "",
              locationDescription: "",
              locationAddress: "",
              region: "",
              locationType: "",
              locationStatus: "",
            });
          }}
          className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white shadow-lg"
        >
          <Plus size={18} className="mr-2" />
          Add New Site
        </Button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <div className="w-2 h-2 bg-green-600 rounded-full"></div>
          <p className="text-green-800 font-medium">{successMessage}</p>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-4 top-3.5 text-gray-400" size={20} />
          <Input
            type="text"
            placeholder="Search by location code, state, or description..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* State Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="w-full sm:w-48">
            <Label htmlFor="stateFilter" className="text-gray-700 font-semibold mb-2 block">
              Filter by State
            </Label>
            <Select value={selectedStateFilter === "" ? "all" : selectedStateFilter} onValueChange={(value) => {
              setSelectedStateFilter(value === "all" ? "" : value);
              setPage(1);
            }}>
              <SelectTrigger id="stateFilter">
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {STATE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Sites Grid - Horizontal Cards */}
      <div className="space-y-4">
        {loading && displayedSites.length === 0 ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="animate-spin text-teal-600" size={32} />
          </div>
        ) : displayedSites.length === 0 ? (
          <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
            <Building2 className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 text-lg font-medium">No sites found</p>
            <p className="text-gray-500 mt-2">
              {searchTerm || selectedStateFilter
                ? "Try adjusting your search or state filter"
                : "Create your first site to get started"}
            </p>
          </div>
        ) : (
          displayedSites.map((site) => (
            <div
              key={site.siteId}
              className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-300 hover:border-blue-300"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Location Code and Address - Same Row */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-2">
                      Location Code
                    </p>
                    <p className="text-lg font-bold text-gray-900">{site.locationCode}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-2">
                      Address
                    </p>
                    <p className="text-sm text-gray-700">{site.locationAddress}</p>
                  </div>
                </div>

                {/* Right Section - Actions */}
                <div className="flex gap-2 w-full md:w-auto">
                  <Button
                    onClick={() => handleEditSite(site)}
                    size="sm"
                    className="bg-blue-500 hover:bg-blue-600 text-white gap-1 flex-1 md:flex-none"
                  >
                    <Edit2 size={16} />
                    View/Edit
                  </Button>
                  <Button
                    onClick={() =>
                      setDeleteConfirm({
                        siteId: site.siteId,
                        locationCode: site.locationCode,
                      })
                    }
                    size="sm"
                    className="bg-red-500 hover:bg-red-600 text-white gap-1 flex-1 md:flex-none"
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {filteredSites.length > maxResults && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {Math.max(1, (page - 1) * maxResults + 1)} to{" "}
            {Math.min(page * maxResults, filteredSites.length)} of {filteredSites.length} sites
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              variant="outline"
              size="sm"
            >
              Previous
            </Button>
            <Button
              onClick={() =>
                setPage(Math.min(Math.ceil(filteredSites.length / maxResults), page + 1))
              }
              disabled={page * maxResults >= filteredSites.length}
              variant="outline"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Drawer */}
      {isDrawerOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center md:justify-center">
          <div className="w-full md:max-w-2xl bg-white rounded-t-2xl md:rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-cyan-50">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingSite ? "Edit Site" : "Create New Site"}
              </h2>
              <button
                onClick={closeDrawer}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-6">
              {/* Error Message */}
              {errors.submit && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                  <p className="text-red-800">{errors.submit}</p>
                </div>
              )}

              {/* Location Code */}
              <div className="space-y-2">
                <Label htmlFor="locationCode" className="text-gray-700 font-semibold">
                  Location Code *
                </Label>
                <Input
                  id="locationCode"
                  type="text"
                  placeholder="e.g., LOC001"
                  value={formData.locationCode}
                  onChange={(e) =>
                    setFormData({ ...formData, locationCode: e.target.value })
                  }
                  className={`border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.locationCode ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.locationCode && (
                  <p className="text-red-600 text-sm">{errors.locationCode}</p>
                )}
              </div>


              {/* Old Location Code */}
              <div className="space-y-2">
                <Label htmlFor="oldLocationCode" className="text-gray-700 font-semibold">
                  Old Location Code *
                </Label>
                <Input
                  id="oldLocationCode"
                  type="text"
                  placeholder="e.g., LOC001"
                  value={formData.oldLocationCode}
                  onChange={(e) =>
                    setFormData({ ...formData, oldLocationCode: e.target.value })
                  }
                  className={`border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.oldLocationCode ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.oldLocationCode && (
                  <p className="text-red-600 text-sm">{errors.oldLocationCode}</p>
                )}
              </div>
              {/* State Dropdown */}
              <div className="space-y-2">
                <Label htmlFor="state" className="text-gray-700 font-semibold">
                  State *
                </Label>
                <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })}>
                  <SelectTrigger className={`border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 ${
                    errors.state ? "border-red-500" : "border-gray-300"
                  }`}>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.state && (
                  <p className="text-red-600 text-sm">{errors.state}</p>
                )}
              </div>

              {/* Location Description */}
              <div className="space-y-2">
                <Label htmlFor="locationDescription" className="text-gray-700 font-semibold">
                  Location Description *
                </Label>
                <textarea
                  id="locationDescription"
                  placeholder="Describe the location (supports special characters)"
                  value={formData.locationDescription}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      locationDescription: e.target.value,
                    })
                  }
                  rows={3}
                  className={`w-full border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                    errors.locationDescription ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.locationDescription && (
                  <p className="text-red-600 text-sm">{errors.locationDescription}</p>
                )}
              </div>

              {/* Location Address */}
              <div className="space-y-2">
                <Label htmlFor="locationAddress" className="text-gray-700 font-semibold">
                  Location Address *
                </Label>
                <textarea
                  id="locationAddress"
                  placeholder="Full address (supports special characters and numbers)"
                  value={formData.locationAddress}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      locationAddress: e.target.value,
                    })
                  }
                  rows={3}
                  className={`w-full border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                    errors.locationAddress ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.locationAddress && (
                  <p className="text-red-600 text-sm">{errors.locationAddress}</p>
                )}
              </div>

              {/* Region Dropdown */}
              <div className="space-y-2">
                <Label htmlFor="region" className="text-gray-700 font-semibold">
                  Region *
                </Label>
                <Select value={formData.region} onValueChange={(value) => setFormData({ ...formData, region: value })}>
                  <SelectTrigger className={`border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 ${
                    errors.region ? "border-red-500" : "border-gray-300"
                  }`}>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.region && (
                  <p className="text-red-600 text-sm">{errors.region}</p>
                )}
              </div>

              {/* Location Type Dropdown */}
              <div className="space-y-2">
                <Label htmlFor="locationType" className="text-gray-700 font-semibold">
                  Location Type *
                </Label>
                <Select value={formData.locationType} onValueChange={(value) => setFormData({ ...formData, locationType: value })}>
                  <SelectTrigger className={`border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 ${
                    errors.locationType ? "border-red-500" : "border-gray-300"
                  }`}>
                    <SelectValue placeholder="Select location type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.locationType && (
                  <p className="text-red-600 text-sm">{errors.locationType}</p>
                )}
              </div>

              {/* Location Status Dropdown */}
              <div className="space-y-2">
                <Label htmlFor="locationStatus" className="text-gray-700 font-semibold">
                  Location Status *
                </Label>
                <Select value={formData.locationStatus} onValueChange={(value) => setFormData({ ...formData, locationStatus: value })}>
                  <SelectTrigger className={`border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 ${
                    errors.locationStatus ? "border-red-500" : "border-gray-300"
                  }`}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.locationStatus && (
                  <p className="text-red-600 text-sm">{errors.locationStatus}</p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 flex gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <Button
                onClick={closeDrawer}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateSite}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {editingSite ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <span>{editingSite ? "Update Site" : "Create Site"}</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete Site?</h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">{deleteConfirm.locationCode}</span>?
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setDeleteConfirm(null)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteSite}
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default SitesPage;
